import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChevronDownIcon from "../../../assets/icons/chevronDownIcon.svg";
import { isMobile } from "react-device-detect";

interface MenuComponentProps {
  menuTitle: string;
  isToggleMenuManually?: boolean;
  menuList: JSX.Element;
  buttonColor?: string;
}

const dropdownClassName =
  "absolute right-0 top-10 z-50 h-auto w-44 rounded-md border border-gray-300 bg-white";

const MenuComponent: React.FC<MenuComponentProps> = ({
  menuTitle,
  isToggleMenuManually = true,
  menuList,
  buttonColor,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => setIsOpen((open) => !open);

  useEffect(() => {
    if (!isOpen) return;

    const handleOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu();
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [isOpen]);

  const handleOnClickToggle = () => {
    if (isMobile || isToggleMenuManually) toggleMenu();
  };

  const menuContent = React.isValidElement(menuList)
    ? React.cloneElement(menuList, { onClose: closeMenu } as { onClose?: () => void })
    : menuList;

  const menuPanel = (
    <div
      ref={menuRef}
      className={dropdownClassName}
      style={
        isMobile && triggerRef.current
          ? {
              position: "fixed",
              top: triggerRef.current.getBoundingClientRect().bottom + 4,
              right:
                window.innerWidth -
                triggerRef.current.getBoundingClientRect().right,
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.1)",
            }
          : { boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.1)" }
      }
    >
      {menuContent}
    </div>
  );

  return (
    <div
      className="relative flex items-center"
      onMouseEnter={() => !isToggleMenuManually && setIsOpen(true)}
      onMouseLeave={() => !isToggleMenuManually && setIsOpen(false)}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOnClickToggle}
        className={`flex w-full items-center justify-between gap-1 rounded-md px-1.5 py-1 text-sm text-white ${buttonColor || "bg-[#243A48]"}`}
      >
        <span className="text-md font-medium">{menuTitle}</span>
        <img src={ChevronDownIcon} alt="" className="h-3 w-3 shrink-0" aria-hidden />
      </button>

      {isOpen &&
        (isMobile ? createPortal(menuPanel, document.body) : menuPanel)}
    </div>
  );
};

export default MenuComponent;
